import { ChangeEvent, useMemo, useState } from "react";
import { faEye } from "@fortawesome/free-solid-svg-icons/faEye";
import { faEyeSlash } from "@fortawesome/free-solid-svg-icons/faEyeSlash";
import Button from "react-bootstrap/Button";
import Alert from "react-bootstrap/Alert";
import Tooltip from "react-bootstrap/Tooltip";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import unionBy from "lodash/unionBy";
import { resources } from "@constants/hardcoded-strings";
import { useModalContext } from "@context/use-modal-context";
import { LabelWidthTypes } from "@enums/label-width-types";
import { ModalTypes } from "@enums/modal-types";
import { UserPermissionTypes } from "@enums/user-permission-types";
import { SelectOption } from "@idw-types/common";
import { IDWSession } from "@idw-types/data/idw-session";
import { GeneralBasicInfoValidationErrors } from "@idw-types/common/validation/verification-service-details-validation-errors";
import { AxnCompany, AxnRelyingParty, SimpleAxnWorkflow } from "@idw-types/data/reference-data/axn";
import { FormattedRpServiceConfiguration, FormattedRpServiceConfigurationProperty } from "@idw-types/data/rp-service-configuration";
import { isNullOrUndefined } from "@utils/object-util";
import { getSelectedOption } from "@utils/options-util";
import { extractRelyingPartiesForPropertyName } from "@utils/permissions-util";
import { mapEntityWithNameAndIdToSelectOption } from "@utils/select-option-mapping-util";
import {
    getActiveRelyingPartyWorkflowOptions,
    injectTemporaryWorkflowOption
} from "@utils/workflow-util";
import { ContentPageBody } from "@containers/common/content-page-body/content-page-body";
import { ContentPageHeader } from "@containers/common/content-page-header/content-page-header";
import { InputFormGroup } from "@containers/common/input-form-group/input-form-group";
import { OrganizationSelectFormGroup, RelyingPartySelectFormGroup } from "@containers/common/select-form-groups";
import { SelectFormGroup } from "@containers/common/select-form-group/select-form-group";
import { AddVerificationWorkflowModalBasic } from "@containers/workflows/add-verification-workflow-modal-basic/add-verification-workflow-modal-basic";
import styles from "./basic-info-tab-container.less";
import { VerificationServiceRedirectUrlContainer } from "@containers/verification-service-details/verification-service-details-tab-container/verification-service-details-general-tab/basic-info-tab-container/verification-service-redirect-url-container/verification-service-redirect-url-container";
import { PageLevelTypes } from "@enums/page-level-types";
import { RedirectUrlInformation } from "@idw-types/redirect-url-information";
import { RedirectUrlUpdateModeTypes, updateRedirectUrls } from "@utils/redirect-urls/redirect-urls-util";
import { RedirectUrlOriginTypes } from "@enums/redirect-url-origin-types";
import { EntityStatusTypes } from "@enums/entity-status-types";

type BasicInfoTabContainerProps = {
    isDisabled: boolean;
    session: IDWSession;
    validationErrors: GeneralBasicInfoValidationErrors;
    axnCompanies: Array<AxnCompany>;
    axnRelyingParties: Array<AxnRelyingParty>;
    initialRpServiceConfigurationWorkflowID: string;
    rpServiceConfigurationState: FormattedRpServiceConfiguration;
    setRpServiceConfigurationState: (state: FormattedRpServiceConfiguration) => void;
    simpleAxnWorkflows: Array<SimpleAxnWorkflow>;
    simpleAxnAdminWorkflows: Array<SimpleAxnWorkflow>;
    redirectUrlsInformation: RedirectUrlInformation;
    handleGetRelyingPartyServicesRequest: (relyingPartyID: string) => void;
    handleGetWorkflowAppliedProfilesRequest: (workflowID: string) => void;
    handleCreateWorkflow: (workflowName: string) => void;
    updateProperties: (properties: Array<{ propertyTypeId: string, newValue: string, added: boolean }>) => void;
};

type BasicInfoTabContainerState = {
    showApiKey: boolean;
    showSharedSecret: boolean;
};

export const BasicInfoTabContainer = (props: BasicInfoTabContainerProps) => {
    const {
        isDisabled,
        session,
        validationErrors,
        axnCompanies,
        axnRelyingParties,
        rpServiceConfigurationState,
        setRpServiceConfigurationState,
        initialRpServiceConfigurationWorkflowID,
        simpleAxnWorkflows,
        simpleAxnAdminWorkflows,
        redirectUrlsInformation,
        handleGetRelyingPartyServicesRequest,
        handleGetWorkflowAppliedProfilesRequest,
        handleCreateWorkflow
    } = props;

    const [labelWidth] = useState(LabelWidthTypes.MEDIUM);

    const [state, setState] = useState<BasicInfoTabContainerState>({
        showApiKey: false,
        showSharedSecret: false
    });

    const serviceProfileKey = `s${rpServiceConfigurationState.id}`;

    const modalContext = useModalContext();

    const selectedRelyingParty = useMemo(() => {
        return mapEntityWithNameAndIdToSelectOption(rpServiceConfigurationState.relyingParty);
    }, [rpServiceConfigurationState.relyingParty]);

    const handleNameChange = (event: ChangeEvent<HTMLInputElement>) => {
        setRpServiceConfigurationState({
            ...rpServiceConfigurationState,
            serviceName: event.target.value
        });
    };

    const handleRelyingPartyChange = (newValue: SelectOption) => {
        if (newValue && rpServiceConfigurationState.relyingParty?.id !== newValue.value) {
            handleGetRelyingPartyServicesRequest(newValue.value);
        }
    };

    const handleApiKeyChange = (event: ChangeEvent<HTMLInputElement>) => {
        setRpServiceConfigurationState({
            ...rpServiceConfigurationState,
            apiKey: event.target.value
        });
    };

    const handleSharedSecretChange = (event: ChangeEvent<HTMLInputElement>) => {
        setRpServiceConfigurationState({
            ...rpServiceConfigurationState,
            sharedSecret: event.target.value,
        });
    };

    const toggleShowApiKey = () => {
        setState({ ...state, showApiKey: !state.showApiKey });
    };

    const toggleShowSharedSecret = () => {
        setState({ ...state, showSharedSecret: !state.showSharedSecret });
    };

    const canAddWorkflowDueToLimits = useMemo(() => {
        const unionedWorkflows = unionBy(simpleAxnAdminWorkflows, simpleAxnWorkflows, 'id');
        return session.limits.workflows > unionedWorkflows.length;
    }, [simpleAxnWorkflows, simpleAxnAdminWorkflows]);

    const workflowOptions = useMemo(() => {
        return getActiveRelyingPartyWorkflowOptions(
            simpleAxnWorkflows,
            simpleAxnAdminWorkflows,
            rpServiceConfigurationState.relyingParty?.id,
            rpServiceConfigurationState.workflow?.name
        );
    }, [simpleAxnWorkflows, simpleAxnAdminWorkflows, rpServiceConfigurationState.relyingParty, rpServiceConfigurationState.workflow?.name]);

    const selectedWorkflow = useMemo(() => {
        if (rpServiceConfigurationState.workflowID === null && rpServiceConfigurationState.workflow?.name) {
            return injectTemporaryWorkflowOption(rpServiceConfigurationState.workflow?.name);
        } else {
            return getSelectedOption(workflowOptions, rpServiceConfigurationState.workflowID);
        }

    }, [workflowOptions, rpServiceConfigurationState.workflowID, rpServiceConfigurationState.workflow?.name]);

    const onSelectedWorkflowChange = (option: SelectOption) => {
        if (!isNullOrUndefined(option?.value)) {
            if (rpServiceConfigurationState.workflowID !== option.value) {
                handleGetWorkflowAppliedProfilesRequest(option.value);
            }
        } else {
            handleCreateWorkflow(null);
        }
    };

    const createVerificationWorkflowButton = useMemo(() => {
        const renderAddVerificationWorkflowModal = (onModalClose: () => void) => {
            return (
                <AddVerificationWorkflowModalBasic
                    onModalClose={onModalClose}
                    onWorkflowSettingApplied={handleCreateWorkflow}
                    workflowName={rpServiceConfigurationState.workflow?.name}
                />
            );
        };

        const onCreateModalClick = () => {
            modalContext.setModalState({
                modalType: ModalTypes.AddBasicVerificationWorkflowAtApiKeyLevel,
                renderFunction: renderAddVerificationWorkflowModal
            });
        };

        const actualButton = (
            <Button
                id="create-new-workflow"
                variant={"primary"}
                disabled={isDisabled || !!rpServiceConfigurationState.workflow?.name || !canAddWorkflowDueToLimits}
                onClick={onCreateModalClick}>
                {resources.common.inputs.workflow.createWorkflowButtonText}
            </Button>
        );

        if (canAddWorkflowDueToLimits) {
            return (
                <div className={styles.createWorkflowButton}>
                    {actualButton}
                </div>
            );
        } else {
            const renderTooltip = (props: any) => {
                return (
                    <Tooltip {...props}>
                        {resources.common.inputs.workflow.createWorkflowButtonDisabledText}
                    </Tooltip>
                );
            };

            return (
                <div className={styles.createWorkflowButton}>
                    <OverlayTrigger overlay={renderTooltip}>
                        {actualButton}
                    </OverlayTrigger>
                </div>
            );
        }

    }, [
        isDisabled,
        rpServiceConfigurationState
    ]);

    const allowedRelyingParties = useMemo(() => {
        if (axnRelyingParties) {
            const validCompanies = axnCompanies.filter(company => company.type === "RP");
            const validCompanyIds = validCompanies.map(vc => vc.id);

            const allowedRelyingParties = extractRelyingPartiesForPropertyName(
                UserPermissionTypes.MODIFY_SERVICES,
                session.user.axnAdminUserRPProperties,
                axnRelyingParties
            ).filter(relyingParty => validCompanyIds.includes(relyingParty.companyID) && (relyingParty.status === EntityStatusTypes.ACTIVE));

            return allowedRelyingParties;
        }

        return [];
    }, [
        session.user.axnAdminUserRPProperties,
        axnRelyingParties,
        axnCompanies
    ]);

    const handleRedirectChangeAtIndex = (newRedirectUrl: string, index: number, _: string, redirectUrlOrigin: RedirectUrlOriginTypes) => {
        if (redirectUrlOrigin === RedirectUrlOriginTypes.PROPERTY_REDIRECT_URL) {
            const newProperties = updateRedirectUrls(
                rpServiceConfigurationState.properties,
                serviceProfileKey,
                RedirectUrlUpdateModeTypes.EDIT,
                PageLevelTypes.APIKEY,
                newRedirectUrl,
                index
            );

            setRpServiceConfigurationState({
                ...rpServiceConfigurationState,
                properties: newProperties as Array<FormattedRpServiceConfigurationProperty>
            });
        } else if (redirectUrlOrigin === RedirectUrlOriginTypes.CALLBACK_URL) {
            setRpServiceConfigurationState({
                ...rpServiceConfigurationState,
                callBackURL: newRedirectUrl
            });
        } else if (redirectUrlOrigin === RedirectUrlOriginTypes.REDIRECT) {
            const redirects = rpServiceConfigurationState.redirects.map((redirect, i) => {
                if (i === index) {
                    return {
                        ...redirect,
                        redirectURI: newRedirectUrl
                    };
                }
                return redirect;
            });

            setRpServiceConfigurationState({
                ...rpServiceConfigurationState,
                redirects
            });
        }

    };


    const removeRedirectUrl = (rpServiceConfigurationState: FormattedRpServiceConfiguration, index: number, redirectUrlOrigin: RedirectUrlOriginTypes): FormattedRpServiceConfiguration => {
        if (redirectUrlOrigin === RedirectUrlOriginTypes.PROPERTY_REDIRECT_URL) {
            const newProperties = updateRedirectUrls(
                rpServiceConfigurationState.properties,
                serviceProfileKey,
                RedirectUrlUpdateModeTypes.REMOVE,
                PageLevelTypes.APIKEY,
                null,
                index
            );

            return {
                ...rpServiceConfigurationState,
                properties: newProperties as Array<FormattedRpServiceConfigurationProperty>
            };
        } else if (redirectUrlOrigin === RedirectUrlOriginTypes.REDIRECT) {
            const redirects = rpServiceConfigurationState.redirects.filter((_, i) => i !== index);

            return {
                ...rpServiceConfigurationState,
                redirects
            };
        }
        else if (redirectUrlOrigin === RedirectUrlOriginTypes.CALLBACK_URL) {
            let newCallbackURL = null;
            const redirectUrl = redirectUrlsInformation.redirectUrls
                .find(baseRedirectUrl => [
                    RedirectUrlOriginTypes.PROPERTY_REDIRECT_URL,
                    RedirectUrlOriginTypes.REDIRECT
                ].includes(baseRedirectUrl.redirectUrlOrigin));

            if (redirectUrl) {
                const { index, redirectUrlOrigin, url } = redirectUrl;
                newCallbackURL = url;
                const updatedRpService = removeRedirectUrl(rpServiceConfigurationState, index, redirectUrlOrigin);
                return {
                    ...updatedRpService,
                    callBackURL: newCallbackURL
                }
            } else {
                return {
                    ...rpServiceConfigurationState,
                    callBackURL: newCallbackURL
                }
            }
        }

        return rpServiceConfigurationState;
    }

    const handleRemoveRedirect = (index: number, redirectUrlOrigin: RedirectUrlOriginTypes) => {
        const updatedRpService = removeRedirectUrl(rpServiceConfigurationState, index, redirectUrlOrigin);

        setRpServiceConfigurationState(updatedRpService);
    };

    const handleAddRedirect = (newRedirectUrl: string) => {
        if (rpServiceConfigurationState.callBackURL === null) {
            setRpServiceConfigurationState({
                ...rpServiceConfigurationState,
                callBackURL: newRedirectUrl
            });
        } else {
            const newProperties = updateRedirectUrls(
                rpServiceConfigurationState.properties,
                serviceProfileKey,
                RedirectUrlUpdateModeTypes.ADD,
                PageLevelTypes.APIKEY,
                newRedirectUrl
            );
            setRpServiceConfigurationState({
                ...rpServiceConfigurationState,
                properties: newProperties as Array<FormattedRpServiceConfigurationProperty>
            });
        }

    };

    const selectedOrganization = useMemo(() => {
        if (rpServiceConfigurationState.organization) {
            return mapEntityWithNameAndIdToSelectOption(rpServiceConfigurationState.organization);
        }
        return null;
    }, [rpServiceConfigurationState.organization]);

    const resource = resources.pages.verificationServiceDetails.tabs.general.subTabs.basicInfo;

    return (
        <div>
            <ContentPageHeader title={resource.title} />
            <ContentPageBody isMaxWidth={true}>
                <InputFormGroup
                    controlId={"verification-service-details-service-name"}
                    automationId={"verification-service-details-service-name"}
                    label={resource.inputs.serviceName.label}
                    ariaLabel={resource.inputs.serviceName.label}
                    value={rpServiceConfigurationState.serviceName}
                    disabled={isDisabled}
                    isRequired={true}
                    labelWidth={labelWidth}
                    placeholder={resource.inputs.serviceName.placeholder}
                    onChange={handleNameChange}
                />
                {validationErrors.serviceNameError}

                <OrganizationSelectFormGroup
                    idPrefix="verification-service-details"
                    onChange={() => { }}
                    axnCompanies={axnCompanies}
                    selectedOption={selectedOrganization}
                    disabled={true}
                    isRequired={false}
                    isSearchable={true}
                    isClearable={true}
                    labelWidth={labelWidth}
                />

                <RelyingPartySelectFormGroup
                    idPrefix={"verification-service-details"}
                    axnRelyingParties={allowedRelyingParties}
                    disabled={isDisabled || selectedWorkflow !== null}
                    isClearable={false}
                    isRequired={true}
                    isSearchable={true}
                    labelWidth={labelWidth}
                    onChange={handleRelyingPartyChange}
                    selectedOption={selectedRelyingParty}
                />

                <SelectFormGroup
                    controlId={"verification-service-details-workflow"}
                    automationId={"verification-service-details-workflow"}
                    ariaLabel={resources.common.inputs.workflow.label}
                    label={resources.common.inputs.workflow.label}
                    disabled={isDisabled || initialRpServiceConfigurationWorkflowID !== null && selectedWorkflow !== null}
                    isClearable={true}
                    isRequired={false}
                    isSearchable={true}
                    labelWidth={labelWidth}
                    onChange={onSelectedWorkflowChange}
                    options={workflowOptions}
                    selectedOption={selectedWorkflow}
                />
                {workflowOptions.length === 0 && <Alert variant={"warning"}>
                    <span>{resources.common.inputs.workflow.noWorkflowFoundErrorMessage}</span>
                </Alert>}


                {!selectedWorkflow && initialRpServiceConfigurationWorkflowID !== null && <Alert variant={"warning"}>
                    <span>{`The Workflow ${initialRpServiceConfigurationWorkflowID} selected is not found`}</span>
                </Alert>}
                {(
                    !selectedWorkflow &&
                    session.userPermissions.canCreateWorkflows &&
                    session.userPermissions.canCreateWorkflowFromScratch &&
                    !isDisabled
                ) && (
                        <div>{createVerificationWorkflowButton}</div>
                    )}
                <InputFormGroup
                    controlId={"verification-service-details-api-key"}
                    automationId={"verification-service-details-api-key"}
                    label={resource.inputs.apiKey.label}
                    ariaLabel={resource.inputs.apiKey.label}
                    value={state.showApiKey ? rpServiceConfigurationState.apiKey : "XXXXXXXXXXXXXX"}
                    disabled={isDisabled || !state.showApiKey || !rpServiceConfigurationState.permissions.canModifyAPIKey}
                    isRequired={true}
                    labelWidth={labelWidth}
                    placeholder={resource.inputs.apiKey.placeholder}
                    onChange={handleApiKeyChange}
                    afterInputIconDisabled={false}
                    afterInputIcon={state.showApiKey ? faEyeSlash : faEye}
                    afterInputIconOnClick={toggleShowApiKey}
                />
                {validationErrors.apiKeyError}
                <InputFormGroup
                    controlId={"verification-service-details-shared-secret"}
                    automationId={"verification-service-details-shared-secret"}
                    label={resource.inputs.sharedSecret.label}
                    ariaLabel={resource.inputs.sharedSecret.label}
                    value={state.showSharedSecret ? rpServiceConfigurationState.sharedSecret : "XXXXXXXXXXXXXX"}
                    disabled={isDisabled || !state.showSharedSecret}
                    isRequired={true}
                    labelWidth={labelWidth}
                    placeholder={resource.inputs.sharedSecret.placeholder}
                    onChange={handleSharedSecretChange}
                    afterInputIconDisabled={false}
                    afterInputIcon={state.showSharedSecret ? faEyeSlash : faEye}
                    afterInputIconOnClick={toggleShowSharedSecret}
                />
                {validationErrors.sharedSecretError}
            </ContentPageBody>
            <VerificationServiceRedirectUrlContainer
                isDisabled={isDisabled}
                isBelongToWorkflow={rpServiceConfigurationState.workflowID !== null}
                pageLevel={PageLevelTypes.APIKEY}
                redirectUrlsInformation={redirectUrlsInformation}
                handleRemoveRedirect={handleRemoveRedirect}
                handleAddRedirect={handleAddRedirect}
                handleRedirectChangeAtIndex={handleRedirectChangeAtIndex}
            />
        </div>
    );
};